import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLang, scrollToId } from "@/lib/lang";
import { bookingHref } from "@/content";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex rounded-full border border-line p-1 font-mono text-xs" data-testid="lang-toggle">
      {["ro", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 uppercase transition-colors duration-300 ${lang === l ? "bg-ink text-bg" : "text-zinc-300 hover:text-ink"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled ? "border-b border-line bg-bg/75 backdrop-blur-xl" : "border-b border-transparent"}`}>
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("top")} data-testid="header-logo-link"><Logo /></button>
        <nav className="hidden items-center gap-8 lg:flex">
          {t.nav.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}-link`} className="link-underline text-sm text-zinc-300 transition-colors hover:text-ink">
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <span className="hidden sm:block">
            <a href={bookingHref()} target="_blank" rel="noopener noreferrer" data-testid="header-book-btn" className="btn-gold">
              {t.cta} <ArrowUpRight size={16} />
            </a>
          </span>
          <button className="rounded-full border border-line p-2 text-ink lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="border-t border-line bg-bg px-5 pb-8 pt-4 lg:hidden" data-testid="mobile-menu">
            {t.nav.map((n) => (
              <button key={n.id} onClick={() => go(n.id)} data-testid={`mobile-nav-${n.id}-link`} className="block w-full border-b border-line py-4 text-left font-display text-2xl text-ink">
                {n.label}
              </button>
            ))}
            <a href={bookingHref()} target="_blank" rel="noopener noreferrer" data-testid="mobile-book-btn" className="btn-gold mt-6 w-full justify-center">
              {t.cta} <ArrowUpRight size={16} />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
